/* ============================================================
   Cart Dropdown — custom renderer replacing the base theme's
   adjustCartDropDown() (assets/shop.js). Rebinds BOTH
   window.adjustCartDropDown and window.Shopify.adjustCartDropDown
   at deferred-execute time, so every base-theme caller routes here.
   MUST load as a `defer` script tag AFTER shop.js's tag in
   layout/theme.liquid (see the 2026-07-17 design spec's timing
   invariant). Pure helpers are Node-testable:
   node --test test/cart-dropdown.test.mjs
   ============================================================ */
(function (root, factory) {
  var api = factory();
  if (typeof module === "object" && module.exports) {
    module.exports = api; // Node (unit tests)
  } else {
    root.CartDropdown = api; // Browser
    api.takeover(root);
  }
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";

  function escapeHtml(s) {
    if (s === null || s === undefined) return "";
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function buildLineItem(item) {
    var variantTitle = item.variant_title;
    if (!variantTitle || variantTitle === "Default Title") variantTitle = null;
    var lineTotal = item.final_line_price != null ? item.final_line_price : item.line_price;
    var originalLineTotal = item.original_line_price != null ? item.original_line_price : lineTotal;
    return {
      key: item.key,
      url: item.url,
      image: item.image || null,
      title: item.product_title || item.title,
      variantTitle: variantTitle,
      quantity: item.quantity,
      unitPrice: item.discounted_price != null ? item.discounted_price : item.price,
      lineTotal: lineTotal,
      originalLineTotal: originalLineTotal,
      discounted: originalLineTotal > lineTotal
    };
  }

  function buildCartState(cart) {
    return {
      itemCount: cart.item_count,
      subtotal: cart.total_price,
      items: cart.items.map(buildLineItem),
      empty: cart.items.length === 0
    };
  }

  function renderItemHTML(vm, formatMoney) {
    var title = escapeHtml(vm.title);
    var priceLine = vm.quantity + " × " + formatMoney(vm.unitPrice) + " — ";
    if (vm.discounted) {
      priceLine += '<s class="cd-row__compare">' + formatMoney(vm.originalLineTotal) + "</s> " +
        '<span class="cd-row__line-total cd-row__line-total--sale">' + formatMoney(vm.lineTotal) + "</span>";
    } else {
      priceLine += '<span class="cd-row__line-total">' + formatMoney(vm.lineTotal) + "</span>";
    }
    return '<div class="cd-row" data-cd-key="' + escapeHtml(vm.key) + '" data-cd-qty="' + vm.quantity + '">' +
      '<div class="cd-row__img">' +
        (vm.image ? '<img loading="lazy" src="' + escapeHtml(vm.image) + '" alt="' + title + '">' : "") +
      "</div>" +
      '<div class="cd-row__body">' +
        '<a class="cd-row__title" href="' + escapeHtml(vm.url) + '">' + title + "</a>" +
        (vm.variantTitle ? '<span class="cd-row__variant">' + escapeHtml(vm.variantTitle) + "</span>" : "") +
        '<div class="cd-row__controls">' +
          '<span class="cd-qty">' +
            '<button type="button" class="cd-qty__btn" data-cd-minus aria-label="Decrease quantity">−</button>' +
            '<span class="cd-qty__value">' + vm.quantity + "</span>" +
            '<button type="button" class="cd-qty__btn" data-cd-plus aria-label="Increase quantity">+</button>' +
          "</span>" +
          '<span class="cd-row__price">' + priceLine + "</span>" +
        "</div>" +
      "</div>" +
      '<button type="button" class="cd-row__remove" data-cd-remove aria-label="Remove ' + title + '">' +
        "&times;</button>" +
    "</div>";
  }

  function renderListHTML(state, formatMoney) {
    var html = "";
    for (var i = 0; i < state.items.length; i++) {
      html += renderItemHTML(state.items[i], formatMoney);
    }
    return html;
  }

  // Shopify's /cart/change.js silently clamps quantity to available stock
  // (200 + clamped cart) on tracked/deny variants. Compare requested vs
  // returned to surface it. Returns the clamped quantity, or null if no clamp.
  function detectClamp(requestedQty, cart, key) {
    if (requestedQty <= 0) return null; // removal requested — never a clamp
    var line = null;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].key === key) {
        line = cart.items[i];
        break;
      }
    }
    if (!line) return 0; // requested > 0 but the line is gone: sold out
    return line.quantity < requestedQty ? line.quantity : null;
  }

  /* ---------- Clear-cart state machine (pure) ---------- */

  // clearReducer(state, event) -> { state, effect }
  // Illegal (state, event) pairs return the same state with effect "none":
  // a TIMER_DONE arriving after CLICK_UNDO finds state "idle" and does
  // nothing, so the undo/timer race is structurally impossible.
  // "committing" is the /cart/clear.js round-trip after the timer fires —
  // CLICK_UNDO is illegal there (and its face renders no Undo button), so
  // undo-after-the-request-was-sent can't happen either.
  // "committed" exists only for the PAGE_HIDE-beacon → PAGE_SHOW interval;
  // COMMIT_OK returns to "idle" (visibility of the control is driven by
  // cart emptiness, and a second clear in one session must work).
  var CLEAR_TRANSITIONS = {
    idle: {
      CLICK_CLEAR: { state: "confirm", effect: "none" }
    },
    confirm: {
      CLICK_CONFIRM: { state: "countdown", effect: "startTimer" },
      CLICK_KEEP: { state: "idle", effect: "none" },
      DISMISS: { state: "idle", effect: "none" }
    },
    countdown: {
      CLICK_UNDO: { state: "idle", effect: "cancelTimer" },
      TIMER_DONE: { state: "committing", effect: "commit" },
      PAGE_HIDE: { state: "committed", effect: "commitBeacon" },
      PAGE_SHOW: { state: "idle", effect: "render" }
    },
    committing: {
      COMMIT_OK: { state: "idle", effect: "none" },
      COMMIT_FAIL: { state: "idle", effect: "render" },
      PAGE_HIDE: { state: "committed", effect: "commitBeacon" },
      PAGE_SHOW: { state: "idle", effect: "render" }
    },
    committed: {
      PAGE_SHOW: { state: "idle", effect: "render" }
    }
  };

  function clearReducer(state, event) {
    var row = CLEAR_TRANSITIONS[state];
    var t = row && row[event];
    if (!t) return { state: state, effect: "none" };
    return { state: t.state, effect: t.effect };
  }

  function buildClearHTML(state, itemCount) {
    if (state === "idle") {
      return '<button type="button" class="cd-clear__link" data-cd-clear>Clear cart</button>';
    }
    if (state === "confirm") {
      var prompt = itemCount === 1
        ? "Remove this item?"
        : "Remove all " + itemCount + " items?";
      return '<span class="cd-clear__prompt">' + escapeHtml(prompt) + "</span>" +
        '<button type="button" class="cd-clear__danger" data-cd-clear-confirm>Clear</button>' +
        '<button type="button" class="cd-clear__keep" data-cd-clear-keep>Keep</button>';
    }
    if (state === "countdown") {
      return '<span class="cd-clear__prompt">Clearing cart…</span>' +
        '<button type="button" class="cd-clear__undo" data-cd-clear-undo>Undo</button>' +
        '<div class="cd-clear__bar"><div class="cd-clear__bar-fill"></div></div>';
    }
    if (state === "committing") {
      // Request in flight: the undo window is over — no Undo button, so an
      // undo-after-send is impossible at the DOM level too.
      return '<span class="cd-clear__prompt">Clearing cart…</span>';
    }
    return ""; // committed — page is hidden or unloading; nothing interactive
  }

  /* ---------- Browser-only below (no-ops in Node) ---------- */

  var renderSeq = 0;
  var changePending = false;
  var clearState = "idle";
  var clearTimer = null;
  var clearItemCount = 0; // latest known cart quantity, for the confirm prompt
  var clearOrigin = null; // the [data-cd-clear-root] the user last interacted with
  var clearAnnouncer = null;
  var pageLocked = false;
  var clearAnnounceTimer = null;

  function formatMoneyBrowser(cents) {
    var S = typeof Shopify !== "undefined" ? Shopify : null;
    if (S && typeof S.formatMoney === "function") {
      return S.formatMoney(cents, S.money_format);
    }
    return "$" + (cents / 100).toFixed(2);
  }

  function applyState(state) {
    clearItemCount = state.itemCount;
    var listHTML = renderListHTML(state, formatMoneyBrowser);
    document.querySelectorAll(".cart-qty").forEach(function (el) {
      el.textContent = state.itemCount;
    });
    document.querySelectorAll(".cart-container").forEach(function (container) {
      var list = container.querySelector(".product-list");
      var subtotal = container.querySelector(".cart__subtotal");
      var footer = container.querySelector(".cart__footer");
      var empty = container.querySelector(".cart__empty");
      // innerHTML, not textContent: money_format may contain markup, and the
      // base renderer used .html() here (shop.js:432).
      if (subtotal) subtotal.innerHTML = formatMoneyBrowser(state.subtotal);
      if (list) {
        list.innerHTML = listHTML;
        list.classList.toggle("hide", state.empty);
      }
      if (footer) footer.classList.toggle("hide", state.empty);
      if (empty) empty.classList.toggle("hide", !state.empty);
    });
    document.querySelectorAll("[data-cd-clear-root]").forEach(function (el) {
      el.classList.toggle("hide", state.empty);
    });
    syncClearUI();
  }

  function syncClearUI() {
    // Face key includes the count so a confirm prompt updates if the cart
    // changes; a countdown re-render restarts the drain bar (cosmetic only —
    // the setTimeout is the source of truth; accepted in the spec).
    var faceKey = clearState + ":" + clearItemCount;
    document.querySelectorAll("[data-cd-clear-root]").forEach(function (root) {
      if (root.getAttribute("data-cd-face") !== faceKey) {
        root.setAttribute("data-cd-face", faceKey);
        root.innerHTML = buildClearHTML(clearState, clearItemCount);
      }
    });
    // committing keeps every countdown lockdown guard: the request is in
    // flight and navigating/mutating is just as hazardous as mid-countdown.
    var locking = clearState === "countdown" || clearState === "committing";
    document.querySelectorAll(".cart-container").forEach(function (c) {
      c.classList.toggle("cd-clearing", locking);
      // pointer-events CSS doesn't stop keyboard activation: without this,
      // Shift+Tab + Enter could reach Checkout mid-countdown, navigate, and
      // fire the pagehide beacon — the exact hazard the lockdown prevents.
      c.querySelectorAll(".cart-links a").forEach(function (a) {
        if (locking) a.setAttribute("tabindex", "-1");
        else a.removeAttribute("tabindex");
      });
    });
    var page = document.querySelector("[data-cd-cart-page]");
    if (page && locking !== pageLocked) {
      pageLocked = locking;
      lockCartPage(page, locking);
    }
  }

  // The /cart page's controls are server-rendered form elements, so CSS
  // pointer-events alone leaves keyboard activation open. Every mutation
  // here reverts when locking is false (undo / commit failure) — in
  // particular the submits must re-enable or checkout is dead after a
  // failed clear.
  function lockCartPage(page, locking) {
    page.classList.toggle("cd-clearing", locking);
    page.querySelectorAll('input[name="checkout"], input[name="update"], .cart__qty-input').forEach(function (el) {
      el.disabled = locking;
    });
    page.querySelectorAll("a.cart__remove, a.cart__continue").forEach(function (el) {
      if (locking) el.setAttribute("tabindex", "-1");
      else el.removeAttribute("tabindex");
    });
  }

  function focusClearControl(selector) {
    if (!clearOrigin) return;
    var el = clearOrigin.querySelector(selector);
    // offsetParent is null inside a closed collapse / display:none clone —
    // never move focus into a hidden root.
    if (el && el.offsetParent !== null) el.focus();
  }

  // On commit the .cd-clear control hides; if focus is inside it, move it
  // somewhere sensible instead of letting it drop to <body>.
  function rescueClearFocus() {
    var active = document.activeElement;
    if (!active || !active.closest || !active.closest("[data-cd-clear-root]")) return;
    var target = null;
    if (clearOrigin) {
      var container = clearOrigin.closest(".cart-container");
      if (container) target = container.querySelector(".view-cart");
    }
    if (!target) {
      var page = document.querySelector("[data-cd-cart-page]");
      if (page) {
        target = page.querySelector("h1");
        if (target && !target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
      }
    }
    if (target) target.focus();
  }

  function announceClear(message) {
    if (!clearAnnouncer) return;
    clearTimeout(clearAnnounceTimer);
    clearAnnouncer.textContent = "";
    clearAnnounceTimer = setTimeout(function () { clearAnnouncer.textContent = message; }, 30);
  }

  function dispatchClear(event, origin) {
    var result = clearReducer(clearState, event);
    clearState = result.state;
    if (origin) clearOrigin = origin;
    switch (result.effect) {
      case "startTimer":
        clearTimer = setTimeout(function () { dispatchClear("TIMER_DONE"); }, 3000);
        break;
      case "cancelTimer":
        clearTimeout(clearTimer);
        clearTimer = null;
        break;
      case "commit":
        // Rescue focus NOW, before syncClearUI swaps in the button-less
        // committing face: at this instant activeElement is still the Undo
        // button; after the swap it's <body> and the rescue can't see it.
        rescueClearFocus();
        commitClear();
        break;
      case "commitBeacon":
        clearTimeout(clearTimer); // bfcache may resume the timer later; kill it
        clearTimer = null;
        flushClearBeacon();
        break;
      case "render":
        render(); // async re-sync to server truth
        break;
    }
    syncClearUI();
  }

  function commitClear() {
    // Mirrors changeQuantity: bump renderSeq at request time; the clear
    // response IS the (empty) cart — apply it directly, no /cart.js re-fetch.
    var seq = ++renderSeq;
    fetch("/cart/clear.js", { method: "POST" })
      .then(function (res) {
        if (!res.ok) throw new Error("cart clear failed: " + res.status);
        return res.json();
      })
      .then(function (cart) {
        // (Focus was already rescued at the commit dispatch — see the
        // "commit" effect case — because the committing face swap detaches
        // the Undo button long before this response lands.)
        dispatchClear("COMMIT_OK");
        // COMMIT_OK announcement comes from the toast's own live region —
        // announcing here too would double-announce (spec, accessibility).
        if (window.Toast) window.Toast.success("Cart cleared");
        if (seq === renderSeq) applyState(buildCartState(cart));
        // The /cart page's table is server-rendered — reload for its empty
        // state. [data-cd-cart-page] is the unambiguous page hook (the
        // dropdown snippet also carries data-section-type="cart-template").
        if (document.querySelector("[data-cd-cart-page]")) location.reload();
      })
      .catch(function (err) {
        console.error("[cart-dropdown]", err);
        if (window.Toast) window.Toast.error("Couldn't clear cart — try again");
        dispatchClear("COMMIT_FAIL"); // effect "render" re-syncs; state → idle
      });
  }

  // Best-effort flush on page unload. sendBeacon can be present yet refuse
  // (returns false) — fall back to keepalive fetch in both cases.
  function flushClearBeacon() {
    var sent = false;
    if (navigator.sendBeacon) {
      try { sent = navigator.sendBeacon("/cart/clear.js"); } catch (e) { sent = false; }
    }
    if (!sent) {
      try {
        // .catch, not just try/catch: a rejected promise isn't caught by
        // try/catch and would surface as an unhandled rejection mid-unload.
        fetch("/cart/clear.js", { method: "POST", keepalive: true }).catch(function () {});
      } catch (e) { /* unloading */ }
    }
  }

  function render() {
    var seq = ++renderSeq;
    return fetch("/cart.js")
      .then(function (r) {
        if (!r.ok) throw new Error("cart fetch failed: " + r.status);
        return r.json();
      })
      .then(function (cart) {
        if (seq !== renderSeq) return; // a newer render superseded this one
        applyState(buildCartState(cart));
      })
      .catch(function (err) {
        // Leave existing DOM untouched on error (spec: error handling).
        console.error("[cart-dropdown]", err);
      });
  }

  function changeQuantity(key, quantity, row) {
    if (changePending) return;
    changePending = true;
    if (row) row.classList.add("cd-row--busy");
    // Read the title now: the row is replaced by the re-render, and a
    // removed line is absent from the response.
    var titleEl = row ? row.querySelector(".cd-row__title") : null;
    var title = titleEl ? titleEl.textContent : "";
    // Bump the render sequence at request time: any older in-flight render
    // becomes stale now, and a newer render started after us still wins.
    var seq = ++renderSeq;
    fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (res) {
        if (!res.ok) throw new Error("cart change failed: " + res.status);
        return res.json();
      })
      .then(function (cart) {
        changePending = false;
        if (row) row.classList.remove("cd-row--busy");
        if (window.Toast) {
          var clamped = detectClamp(quantity, cart, key);
          if (clamped !== null) {
            window.Toast.show(clamped === 0
              ? "Sold out — removed from your cart"
              : "Only " + clamped + " available — you have them all in your cart");
          } else if (quantity === 0) {
            window.Toast.show("Removed " + title);
          }
        }
        // The change response IS the updated cart — no /cart.js re-fetch.
        if (seq === renderSeq) applyState(buildCartState(cart));
      })
      .catch(function (err) {
        console.error("[cart-dropdown]", err);
        changePending = false;
        if (row) row.classList.remove("cd-row--busy");
        if (window.Toast) window.Toast.error("Couldn't update cart — try again");
        render(); // re-sync to server truth; on failure it leaves the DOM untouched
      });
  }

  function onClick(event) {
    var clearBtn = event.target.closest(
      "[data-cd-clear], [data-cd-clear-keep], [data-cd-clear-confirm], [data-cd-clear-undo]"
    );
    if (clearBtn) {
      event.preventDefault();
      var clearRoot = clearBtn.closest("[data-cd-clear-root]");
      if (clearBtn.hasAttribute("data-cd-clear")) {
        dispatchClear("CLICK_CLEAR", clearRoot);
        announceClear(clearItemCount === 1
          ? "Remove this item?"
          : "Remove all " + clearItemCount + " items?");
        focusClearControl("[data-cd-clear-keep]"); // safe default: Enter keeps
      } else if (clearBtn.hasAttribute("data-cd-clear-keep")) {
        dispatchClear("CLICK_KEEP", clearRoot);
        focusClearControl("[data-cd-clear]");
      } else if (clearBtn.hasAttribute("data-cd-clear-confirm")) {
        if (changePending) return; // a qty change is in flight — spec race guard
        dispatchClear("CLICK_CONFIRM", clearRoot);
        announceClear("Clearing cart. Undo available.");
        focusClearControl("[data-cd-clear-undo]");
      } else {
        dispatchClear("CLICK_UNDO", clearRoot);
        focusClearControl("[data-cd-clear]");
      }
      return;
    }
    // Outside-click dismissal of the confirm face lives HERE, not in a
    // second document listener: dispatching CLICK_CLEAR swaps the root's
    // innerHTML, which detaches the clicked button — a later listener on
    // the same event would see target.closest("[data-cd-clear-root]")
    // === null and wrongly treat the opening click as an outside click,
    // self-dismissing the confirm face on every open. In THIS position the
    // in-root check is reliable (no same-event DOM mutation has happened
    // for non-clear clicks), so a click on the prompt text or between the
    // pills doesn't dismiss; the collapse toggle and any click that closes
    // the dropdown are genuinely outside and do.
    if (clearState === "confirm" &&
        !(event.target.closest && event.target.closest("[data-cd-clear-root]"))) {
      dispatchClear("DISMISS");
    }
    var btn = event.target.closest("[data-cd-minus], [data-cd-plus], [data-cd-remove]");
    if (!btn) return;
    if (clearState === "countdown" || clearState === "committing") return; // rows are inert
    var row = btn.closest(".cd-row");
    if (!row) return;
    event.preventDefault();
    var key = row.getAttribute("data-cd-key");
    var qty = parseInt(row.getAttribute("data-cd-qty"), 10) || 0;
    if (btn.hasAttribute("data-cd-remove")) {
      changeQuantity(key, 0, row);
    } else if (btn.hasAttribute("data-cd-plus")) {
      changeQuantity(key, qty + 1, row);
    } else {
      changeQuantity(key, Math.max(0, qty - 1), row); // minus at 1 removes
    }
  }

  // Escape dismissal is NEW plumbing (the dropdown is a Bootstrap collapse
  // whose hidden.bs.collapse is a jQuery event, invisible to
  // addEventListener; this module is jQuery-free). Outside-click dismissal
  // is handled inside onClick — see the comment there.
  function onDocKeydown(event) {
    if (event.key !== "Escape" || clearState !== "confirm") return;
    // Countdown deliberately ignores Escape: Undo is the only cancel.
    dispatchClear("DISMISS");
    focusClearControl("[data-cd-clear]");
  }

  // Belt over the disabled submits: a still-focused control could submit
  // the cart form mid-countdown (capture phase so it beats theme handlers).
  function onDocSubmit(event) {
    if (clearState !== "countdown" && clearState !== "committing") return;
    if (event.target.closest && event.target.closest("[data-cd-cart-page]")) {
      event.preventDefault();
    }
  }

  function takeover(root) {
    // Rebind BOTH bindings of the base renderer (see spec: the seam).
    root.adjustCartDropDown = render;
    root.Shopify = root.Shopify || {};
    root.Shopify.adjustCartDropDown = render;
    document.addEventListener("click", onClick); // routes clear controls, rows, AND confirm outside-click dismissal
    document.addEventListener("keydown", onDocKeydown);
    document.addEventListener("submit", onDocSubmit, true);
    window.addEventListener("pagehide", function () { dispatchClear("PAGE_HIDE"); });
    window.addEventListener("pageshow", function (e) {
      if (e.persisted) dispatchClear("PAGE_SHOW"); // bfcache restore → re-sync
    });
    function boot() {
      // Persistent polite announcer (toast.js pattern): live regions must
      // exist in the accessibility tree BEFORE the first mutation, and the
      // swapped .cd-clear faces must not be live themselves.
      clearAnnouncer = document.createElement("div");
      clearAnnouncer.className = "glass-toast-announcer";
      clearAnnouncer.setAttribute("aria-live", "polite");
      document.body.appendChild(clearAnnouncer);
      render();
    }
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", boot);
    } else {
      boot();
    }
  }

  return {
    escapeHtml: escapeHtml,
    buildLineItem: buildLineItem,
    buildCartState: buildCartState,
    renderItemHTML: renderItemHTML,
    renderListHTML: renderListHTML,
    detectClamp: detectClamp,
    clearReducer: clearReducer,
    buildClearHTML: buildClearHTML,
    takeover: takeover,
    render: render
  };
});
